import type { Document, SearchResult } from '../../../../api';

/**
 * Ownership and sharing facts of a document as seen by the signed-in user.
 *
 * The list endpoint returns the owner under user_id or owner_id and the
 * shares as id arrays; details arrays carry names when the backend resolved
 * them. This helper reads both shapes so the table cells stay declarative.
 */
export interface DocumentAccess {
  /** The signed-in user owns the document */
  isOwner: boolean;
  /** The document belongs to someone else and was shared with the user */
  isSharedWithMe: boolean;
  /** Number of users the owner shared the document with */
  userCount: number;
  /** Number of groups the owner shared the document with */
  groupCount: number;
  /** The owner shared the document with at least one user or group */
  hasShares: boolean;
  /** The signed-in user is listed directly among the shared users */
  viaDirect: boolean;
  /** Names of the user's groups through which the document is shared */
  viaGroupNames: string[];
}

interface AccessFields {
  user_id?: string | null;
  owner_id?: string | null;
  created_by?: string | null;
  shared_with_users?: string[] | null;
  shared_with_groups?: string[] | null;
  shared_with_groups_details?: Array<Record<string, string>> | null;
}

export function getDocumentAccess(
  doc: Document | SearchResult,
  currentUserId?: string | null,
  currentUserGroupIds?: string[] | null
): DocumentAccess {
  const fields = doc as AccessFields;
  const ownerId = fields.owner_id || fields.user_id || fields.created_by || null;
  const isOwner = Boolean(currentUserId && ownerId && ownerId === currentUserId);
  const sharedUsers = Array.isArray(fields.shared_with_users) ? fields.shared_with_users : [];
  const sharedGroups = Array.isArray(fields.shared_with_groups) ? fields.shared_with_groups : [];
  const details = Array.isArray(fields.shared_with_groups_details) ? fields.shared_with_groups_details : [];
  const myGroups = new Set(currentUserGroupIds ?? []);
  const viaGroupNames = sharedGroups
    .filter((groupId) => myGroups.has(groupId))
    .map((groupId) => {
      const detail = details.find((d) => d && (d.id === groupId || d.group_id === groupId));
      return detail?.name || detail?.display_name || detail?.displayName || groupId;
    });
  return {
    isOwner,
    isSharedWithMe: Boolean(currentUserId && ownerId && !isOwner),
    userCount: sharedUsers.length,
    groupCount: sharedGroups.length,
    hasShares: sharedUsers.length + sharedGroups.length > 0,
    viaDirect: Boolean(currentUserId && sharedUsers.includes(currentUserId)),
    viaGroupNames,
  };
}
